import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePickerButton, Button } from 'design-system-web';
import { Dropdown } from 'components/form';
import type { DropdownItem } from 'design-system-web/components/Dropdown/Dropdown';
import { SortDirectionEnum } from 'constants-activate';

import { FilterState } from './types';
import styles from './InteractionFilters.scss';

interface Props {
  isReservableContent: boolean;
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
}
export type ReceiptSortOptions = {
  label: string;
  value: {
    key: string;
    dir: SortDirectionEnum;
  };
};
const SortOptions: ReceiptSortOptions[] = [
  {
    label: 'web.content.interaction.receipt.filters.sortBy.bookingOrderNewest',
    value: { key: '_created', dir: SortDirectionEnum.Desc },
  },
  {
    label: 'web.content.interaction.receipt.filters.sortBy.bookingOrderOldest',
    value: { key: '_created', dir: SortDirectionEnum.Asc },
  },
  {
    label:
      'web.content.interaction.receipt.filters.sortBy.reservationDateNewest',
    value: { key: 'startDate', dir: SortDirectionEnum.Desc },
  },
  {
    label:
      'web.content.interaction.receipt.filters.sortBy.reservationDateOldest',
    value: { key: 'startDate', dir: SortDirectionEnum.Asc },
  },
];

function InteractionFilters({
  isReservableContent,
  filters,
  onFiltersChange,
}: Props) {
  const { t } = useTranslation();

  const sortOptions = useMemo(
    () => [
      SortOptions[0],
      SortOptions[1],
      ...(isReservableContent ? [SortOptions[2], SortOptions[3]] : []),
    ],
    [isReservableContent]
  );
  const handleSortChange = (
    item: DropdownItem<ReceiptSortOptions['value']>
  ) => {
    const selected = item?.value;

    if (!selected) return;

    onFiltersChange({
      ...filters,
      sortBy: selected.key,
      sortDirection: selected.dir,
    });
  };

  const handleReservationStartDateTopChange = (date?: Date) => {
    onFiltersChange({
      ...filters,
      reservationDateRange: {
        ...filters.reservationDateRange,
        startDate: date,
      },
    });
  };

  const handleBookingEndDateChange = (date?: Date) => {
    onFiltersChange({
      ...filters,
      bookingDateRange: { ...filters.bookingDateRange, endDate: date },
    });
  };

  const handleClearStartDate = () => {
    onFiltersChange({
      ...filters,
      reservationDateRange: {
        ...filters.reservationDateRange,
        startDate: undefined,
      },
    });
  };

  const handleClearCreatedDate = () => {
    onFiltersChange({
      ...filters,
      bookingDateRange: { ...filters.bookingDateRange, endDate: undefined },
    });
  };

  const getCurrentSortValue = () => {
    const current = sortOptions.find(
      opt =>
        opt.value.key === filters.sortBy &&
        opt.value.dir === filters.sortDirection
    );

    return current ? current.value : sortOptions[0].value;
  };

  return (
    <div className={styles.controls}>
      <div className={styles.filtersRow}>
        <div className={styles.sortContainer}>
          <Dropdown
            items={sortOptions}
            value={getCurrentSortValue()}
            onChange={handleSortChange}
            className={styles.sortDropdown}
            placeholder="web.content.interaction.receipt.filters.sortByDate"
          />
        </div>
        {isReservableContent && (
          <div className={styles.dateField}>
            <div className={styles.dateFieldHeader}>
              {filters.reservationDateRange.startDate && (
                <Button
                  variant="text"
                  size="medium"
                  onClick={handleClearStartDate}
                  data-test="clear-start-date"
                  className={styles.clearButton}
                >
                  {t('web.pages.portal.home.me.recentActivity.filters.clear')}
                </Button>
              )}
            </div>
            <DatePickerButton
              value={filters.reservationDateRange.startDate}
              onChange={(d: Date) => handleReservationStartDateTopChange(d)}
              className={styles.datePicker}
              minDate={filters.bookingDateRange.endDate}
              dateInputLabel={t('Start date')}
              data-test="start-date-picker"
            />
          </div>
        )}
        <div className={styles.dateField}>
          <div className={styles.dateFieldHeader}>
            {filters.bookingDateRange.endDate && (
              <Button
                variant="text"
                size="medium"
                onClick={handleClearCreatedDate}
                data-test="clear-created-date"
                className={styles.clearButton}
              >
                {t('web.pages.portal.home.me.recentActivity.filters.clear')}
              </Button>
            )}
          </div>
          <DatePickerButton
            value={filters.bookingDateRange.endDate}
            onChange={(d: Date) => handleBookingEndDateChange(d)}
            className={styles.datePicker}
            dateInputLabel="web.content.interaction.receipt.filters.datePicker.createdDate"
            data-test="created-date-picker"
          />
        </div>
      </div>
    </div>
  );
}

export { InteractionFilters };
